import { useMemo } from 'react';

import { Typography } from '@mui/material';

import { gql } from '@graphql/__generated__';
import {
  type ActivityItemContentFragment,
  Activity_Types_Enum_Enum,
} from '@graphql/__generated__/graphql';
import { ActivityExtraDataContent } from '@modules/_shared/components/feed-v2/components/ActivitiyItemContent/ActivityExtraDataContent';
import { AssignmentActivityItemContent } from '@modules/_shared/components/feed-v2/components/ActivitiyItemContent/AssignmentActivityItemContent';
import { ChangeActivityItemContent } from '@modules/_shared/components/feed-v2/components/ActivitiyItemContent/ChangeActivityItemContent';
import { EmailActivityItemContent } from '@modules/_shared/components/feed-v2/components/ActivitiyItemContent/EmailActivityItemContent';
import { RequalificationChanges } from '@modules/_shared/components/feed-v2/components/RequalificationChanges';
import { parseRequalificationChanges } from '@modules/_shared/components/feed-v2/utils/requalificationParser';
import { useLocale } from '@shared/hooks/locale';

export const ACTIVITY_ITEM_CONTENT_FRAGMENT = gql(/* GraphQL */ `
  fragment ActivityItemContent on activities {
    id
    activity_type
    subject
    extra_data
    ...EmailActivityItemContent
    ...AssignmentActivityItemContent
    ...ChangeActivityItemContent
  }
`);

type ActivityItemContentProps = {
  activity: ActivityItemContentFragment;
  isIndirect: boolean;
  done: boolean;
  count: number;
  onReplyClicked: () => void;
  onForwardClicked: () => void;
};

export const ActivityItemContent = ({
  activity,
  isIndirect,
  done,
  count,
  onReplyClicked,
  onForwardClicked,
}: ActivityItemContentProps) => {
  const { t } = useLocale();

  const content = useMemo(() => {
    switch (activity.activity_type) {
      case Activity_Types_Enum_Enum.Email:
        return (
          <EmailActivityItemContent
            activity={activity}
            onReplyClicked={onReplyClicked}
            onForwardClicked={onForwardClicked}
          />
        );
      case Activity_Types_Enum_Enum.Origination:
      case Activity_Types_Enum_Enum.Note:
      case Activity_Types_Enum_Enum.Call:
      case Activity_Types_Enum_Enum.Visit:
        return null;
      case Activity_Types_Enum_Enum.Task:
        return (
          activity.subject && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
                wordBreak: 'break-word',
              }}
            >
              {activity.subject}
            </Typography>
          )
        );
      case Activity_Types_Enum_Enum.Assignment:
        return (
          <AssignmentActivityItemContent
            activity={activity}
            isIndirect={isIndirect}
            done={done}
          />
        );
      case Activity_Types_Enum_Enum.Requalification:
        {
          /* Qualification changes for requalification activities (only when not grouped) */
        }
        return (
          count === 1 && (
            <RequalificationChanges
              changes={parseRequalificationChanges(activity.extra_data, t)}
            />
          )
        );
      case Activity_Types_Enum_Enum.AgentOptIn:
      case Activity_Types_Enum_Enum.AgentQuizCompleted:
        return <ActivityExtraDataContent extraData={activity.extra_data} />;
      case Activity_Types_Enum_Enum.Change:
        return <ChangeActivityItemContent activity={activity} />;
      case Activity_Types_Enum_Enum.LeadStatusAgent:
        // The title carries the full from/to stage info (like requalification).
        return null;
    }
    return null;
  }, [activity, isIndirect, done, count, t, onReplyClicked, onForwardClicked]);
  return content;
};
